import React from 'react'

import './css/HowItWorks.css'

import { useStagePlayer } from './stagePlayer'
import { Steps } from './Steps'


// How the watermark works, told in five steps that play on their own and
// can be stepped through by hand, on the same clock as the traceability
// scene of the home page (stagePlayer.ts).
//
// A photograph goes through the encoder, a network whose layers light up
// one after the other as the signal runs through it, the light fading
// behind it, and comes out marked. The
// marked copy is put through what a copy goes through: it is photographed
// off a phone's screen, then cropped the way a phone crops. What is left
// goes through the decoder, which lights up the same way, and the
// identifier it reads names who the copy was given to.
//
// The scene is one SVG. Every drawing is placed with a transform computed
// from a layout, so the same drawings serve the wide scene and the stacked
// one. A step only toggles a class on the scene; the stylesheet does the
// rest.

const PHOTO: string = require( './images/HowItWorks.Photo.jpg' )

interface Point {
	x: number
	y: number
}

interface Layout {
	width: number
	height: number
	source: Point
	encoder: Point
	marked: Point
	phone: Point
	decoder: Point
	///@brief   The identifier: fed into the encoder, read out of the decoder.
	idIn: Point
	idOut: Point
	user: Point
	answer: Point
	///@brief   Source to encoder, encoder to marked, marked to phone, phone to
	///         decoder, decoder to the identifier read, that identifier to
	///         the person.
	links: string[]
	///@brief   The identifier fed in: down onto the picture's own link into
	///         the encoder, which it meets at a right angle.
	idLink: string
}

const STEPS: string[] = [
	'Embedding',
	'Watermarked',
	'Screenshot, then crop',
	'Decoding',
	'Identified',
]

///@brief   How long each step stays on screen before the next one plays:
///         the two networks need the time for the signal to run through.
const STAGE_DURATIONS_MS: number[] = [ 3700, 2600, 3800, 3400, 3600 ]
const WRAP_FADE_MS: number = 380

///@brief   Below this width the scene stacks in three rows.
const STACKED_QUERY: string = '(max-width: 640px)'

///@brief   The photograph's card.
const CARD_W: number = 84
const CARD_H: number = 112

///@brief   The phone: its body, and its screen inside.
const PHONE_W: number = 84
const PHONE_H: number = 168
const SCREEN_W: number = 74
const SCREEN_H: number = 152

///@brief   The two networks: nodes per layer. The encoder swells in the
///         middle, where the picture and the identifier are folded
///         together; the decoder narrows down to the identifier it reads.
const ENCODER_LAYERS: number[] = [ 3, 6, 8, 8, 6, 3 ]
const DECODER_LAYERS: number[] = [ 7, 8, 6, 4, 2 ]
///@brief   Distance between layers, and between the nodes of a layer.
const NET_GAP: number = 34
const NET_SPACING: number = 20

const USER_R: number = 28


///@brief   Left to right: the picture, the encoder, the marked copy, the
///         phone, the decoder, the person it was given to.
function wideLayout(): Layout {
	const y: number = 160
	return {
		width: 960,
		height: 300,
		source: { x: 66, y },
		encoder: { x: 240, y },
		marked: { x: 404, y },
		phone: { x: 538, y },
		decoder: { x: 690, y },
		idIn: { x: 129, y: 96 },
		idOut: { x: 812, y },
		user: { x: 890, y },
		answer: { x: 890, y: y + 96 },
		links: [
			`M108 ${ y } H 150`,
			`M330 ${ y } H 358`,
			`M450 ${ y } H 492`,
			`M584 ${ y } H 609`,
			`M771 ${ y } H 792`,
			`M832 ${ y } H 858`,
		],
		idLink: `M129 106 V ${ y }`,
	}
}


///@brief   Three rows, for phones: the picture, the encoder and the marked
///         copy; the phone; the decoder and the person.
function stackedLayout(): Layout {
	return {
		width: 420,
		height: 740,
		source: { x: 56, y: 120 },
		encoder: { x: 200, y: 120 },
		marked: { x: 364, y: 120 },
		phone: { x: 210, y: 360 },
		decoder: { x: 110, y: 600 },
		idIn: { x: 104.5, y: 44 },
		idOut: { x: 240, y: 600 },
		user: { x: 330, y: 600 },
		answer: { x: 330, y: 696 },
		links: [
			'M98 120 H 111',
			'M289 120 H 318',
			'M364 178 V 230 H 210 V 272',
			'M210 448 V 500 H 110 V 526',
			'M191 600 H 222',
			'M258 600 H 302',
		],
		idLink: 'M104.5 54 V 120',
	}
}


function place( p: Point ): React.CSSProperties {
	return { transform: `translate(${ p.x }px, ${ p.y }px)` }
}


///@brief   The photograph on a rounded card, centred on the origin; marked,
///         it also carries the grain of the watermark and its tag.
function Card( { bMarked }: { bMarked: boolean } ): React.JSX.Element {
	return (
		<>
			<image href={ PHOTO } x={ -CARD_W / 2 } y={ -CARD_H / 2 } width={ CARD_W } height={ CARD_H } preserveAspectRatio="xMidYMid slice" clipPath="url(#hiw-card)" />
			{ bMarked && <rect className="HowItWorks-Grain" x={ -CARD_W / 2 } y={ -CARD_H / 2 } width={ CARD_W } height={ CARD_H } fill="url(#hiw-dots)" clipPath="url(#hiw-card)" /> }
			<rect className="HowItWorks-Card-Frame" x={ -CARD_W / 2 } y={ -CARD_H / 2 } width={ CARD_W } height={ CARD_H } rx="8" />
			{ bMarked && (
				<g className="HowItWorks-Tag" >
					<rect x={ CARD_W / 2 - 30 } y={ CARD_H / 2 - 20 } width="24" height="14" rx="7" />
					<text x={ CARD_W / 2 - 18 } y={ CARD_H / 2 - 10 } >id</text>
				</g>
			) }
		</>
	)
}


///@brief   A network, centred on the origin: its layers of nodes, and every
///         edge between neighbouring layers. Each edge is drawn twice — once
///         grey, once as the pulse that will run along it — and each node
///         twice too, at rest and lit. --k is the layer (or the gap between
///         layers) an element belongs to, which sets when its turn comes as
///         the signal runs through; the colours run from the brand's blue at
///         the input to its purple at the output, through `gradientId`.
function Network( { layers, gradientId }: { layers: number[], gradientId: string } ): React.JSX.Element {
	const xs: number[] = layers.map( ( _: number, l: number ): number => ( l - ( layers.length - 1 ) / 2 ) * NET_GAP )
	const ys = ( n: number ): number[] => Array.from( { length: n }, ( _: unknown, i: number ): number => ( i - ( n - 1 ) / 2 ) * NET_SPACING )
	const width: number = ( layers.length - 1 ) * NET_GAP
	const last: number = layers.length - 1

	const edges: React.JSX.Element[] = []
	for ( let l: number = 0; l < last; l++ ) {
		const from: number[] = ys( layers[ l ] )
		const to: number[] = ys( layers[ l + 1 ] )
		from.forEach( ( y1: number, i: number ): void => {
			to.forEach( ( y2: number, j: number ): void => {
				const key: string = `${ l }-${ i }-${ j }`
				edges.push( <line className="HowItWorks-Edge" x1={ xs[ l ] } y1={ y1 } x2={ xs[ l + 1 ] } y2={ y2 } key={ `e${ key }` } /> )
				edges.push( <line className="HowItWorks-Pulse" x1={ xs[ l ] } y1={ y1 } x2={ xs[ l + 1 ] } y2={ y2 } pathLength={ 1 } stroke={ `url(#${ gradientId })` } style={ { '--k': l } as React.CSSProperties } key={ `p${ key }` } /> )
			} )
		} )
	}

	const nodes: React.JSX.Element[] = []
	layers.forEach( ( n: number, l: number ): void => {
		// The input and output layers stand out a little.
		const r: number = l === 0 || l === last ? 5 : 4
		ys( n ).forEach( ( y: number, i: number ): void => {
			nodes.push( <circle className="HowItWorks-Neuron" cx={ xs[ l ] } cy={ y } r={ r } key={ `n${ l }-${ i }` } /> )
			nodes.push( <circle className="HowItWorks-Neuron-Lit" cx={ xs[ l ] } cy={ y } r={ r } fill={ `url(#${ gradientId })` } style={ { '--k': l } as React.CSSProperties } key={ `l${ l }-${ i }` } /> )
		} )
	} )

	return (
		<>
			<defs>
				<linearGradient id={ gradientId } gradientUnits="userSpaceOnUse" x1={ -width / 2 } y1="0" x2={ width / 2 } y2="0" >
					<stop offset="0" stopColor="var(--brand-blue-light)" />
					<stop offset="1" stopColor="var(--brand-purple-light)" />
				</linearGradient>
			</defs>
			{ edges }
			{ nodes }
		</>
	)
}


///@brief   The identifier, as a small tag: the one fed into the encoder,
///         the one read out of the decoder, and the one on the marked copy
///         are the same.
function IdTag(): React.JSX.Element {
	return (
		<>
			<rect x="-18" y="-9" width="36" height="18" rx="9" />
			<text y="3.5" >id</text>
		</>
	)
}


///@brief   Where a network's label goes: under its tallest layer.
function networkLabelY( layers: number[] ): number {
	return ( Math.max( ...layers ) - 1 ) / 2 * NET_SPACING + 24
}


///@brief   The phone, centred on the origin, the marked copy on its screen:
///         dimmed everywhere, bright inside the crop, which starts as the
///         whole screen and closes in on the picture; the flash of the
///         screenshot over it, and the crop's frame with its handles.
function Phone(): React.JSX.Element {
	const sx: number = -SCREEN_W / 2
	const sy: number = -SCREEN_H / 2
	const h: number = 10

	return (
		<>
			<rect className="HowItWorks-Phone-Body" x={ -PHONE_W / 2 } y={ -PHONE_H / 2 } width={ PHONE_W } height={ PHONE_H } rx="12" />
			<g clipPath="url(#hiw-screen)" >
				<rect x={ sx } y={ sy } width={ SCREEN_W } height={ SCREEN_H } fill="rgb(8, 8, 10)" />
				<g className="HowItWorks-Screen" >
					<image className="HowItWorks-Screen-Dim" href={ PHOTO } x={ sx } y={ sy } width={ SCREEN_W } height={ SCREEN_H } preserveAspectRatio="xMidYMid slice" />
					<g clipPath="url(#hiw-crop)" >
						<image href={ PHOTO } x={ sx } y={ sy } width={ SCREEN_W } height={ SCREEN_H } preserveAspectRatio="xMidYMid slice" />
						<rect className="HowItWorks-Grain" x={ sx } y={ sy } width={ SCREEN_W } height={ SCREEN_H } fill="url(#hiw-dots)" />
					</g>
					<g className="HowItWorks-CropFrame" >
						<rect className="HowItWorks-CropFrame-Rect" x={ sx } y={ sy } width={ SCREEN_W } height={ SCREEN_H } />
						<path className="HowItWorks-CropFrame-Handle" d={ `M${ sx } ${ sy + h } V ${ sy } H ${ sx + h }` } />
						<path className="HowItWorks-CropFrame-Handle" d={ `M${ sx + SCREEN_W - h } ${ sy } H ${ sx + SCREEN_W } V ${ sy + h }` } />
						<path className="HowItWorks-CropFrame-Handle" d={ `M${ sx + SCREEN_W } ${ sy + SCREEN_H - h } V ${ sy + SCREEN_H } H ${ sx + SCREEN_W - h }` } />
						<path className="HowItWorks-CropFrame-Handle" d={ `M${ sx + h } ${ sy + SCREEN_H } H ${ sx } V ${ sy + SCREEN_H - h }` } />
					</g>
				</g>
				<rect className="HowItWorks-Flash" x={ sx } y={ sy } width={ SCREEN_W } height={ SCREEN_H } />
			</g>
			<rect className="HowItWorks-Phone-Notch" x="-12" y={ -PHONE_H / 2 + 5 } width="24" height="5" rx="2.5" />
		</>
	)
}


export function HowItWorks(): React.JSX.Element {

	const player = useStagePlayer( STAGE_DURATIONS_MS, WRAP_FADE_MS )
	const { stage, bFading, bSnap, rootRef } = player

	const [ bStacked, setStacked ] = React.useState<boolean>( (): boolean =>
		typeof window.matchMedia === 'function' && window.matchMedia( STACKED_QUERY ).matches
	)

	const layout: Layout = React.useMemo( (): Layout => bStacked ? stackedLayout() : wideLayout(), [ bStacked ] )

	// Wide or stacked, following the viewport.
	React.useEffect( (): ( () => void ) => {
		if ( typeof window.matchMedia !== 'function' ) return (): void => {}
		const query: MediaQueryList = window.matchMedia( STACKED_QUERY )
		const update = (): void => setStacked( query.matches )
		query.addEventListener( 'change', update )
		return (): void => query.removeEventListener( 'change', update )
	}, [] )

	const sceneClass: string = [
		'Frame Scene HowItWorks-Scene',
		`is-stage-${ stage }`,
		bFading ? 'is-fading' : '',
		bSnap ? 'is-snap' : '',
	].filter( Boolean ).join( ' ' )

	return (
		<div className="HowItWorks Scene-Stage" >

			<div className={ sceneClass } ref={ rootRef } data-fade >
				<svg
					className="Scene-Svg"
					viewBox={ `0 0 ${ layout.width } ${ layout.height }` }
					role="img"
					aria-label="A photograph and an identifier go through the encoder network, and the photograph comes out watermarked. The marked copy is screenshotted on a phone and cropped. What is left goes through the decoder network, which reads the identifier back out, and the identifier names the fan the copy was given to."
				>
					<defs>
						<clipPath id="hiw-card" >
							<rect x={ -CARD_W / 2 } y={ -CARD_H / 2 } width={ CARD_W } height={ CARD_H } rx="8" />
						</clipPath>
						<clipPath id="hiw-screen" >
							<rect x={ -SCREEN_W / 2 } y={ -SCREEN_H / 2 } width={ SCREEN_W } height={ SCREEN_H } rx="7" />
						</clipPath>
						{ /* The crop: this rectangle closes in on the picture, and
						     the bright copy of the screen is kept to it. */ }
						<clipPath id="hiw-crop" >
							<rect className="HowItWorks-Crop" x={ -SCREEN_W / 2 } y={ -SCREEN_H / 2 } width={ SCREEN_W } height={ SCREEN_H } rx="3" />
						</clipPath>
						{ /* The grain that stands for the mark. */ }
						<pattern id="hiw-dots" width="5" height="5" patternUnits="userSpaceOnUse" >
							<circle cx="2.5" cy="2.5" r="0.9" fill="var(--brand-purple-light)" />
						</pattern>
					</defs>

					{ layout.links.map( ( d: string, i: number ): React.JSX.Element => (
						<path className={ `HowItWorks-Link HowItWorks-Link-${ i + 1 }` } d={ d } pathLength={ 1 } key={ i } />
					) ) }
					<path className="HowItWorks-Link HowItWorks-Link-Id" d={ layout.idLink } pathLength={ 1 } />
					{ /* The signal along each link, over it: runs when the link
					     opens, then fades. */ }
					{ layout.links.map( ( d: string, i: number ): React.JSX.Element => (
						<path className={ `HowItWorks-Link-Pulse HowItWorks-Link-Pulse-${ i + 1 }` } d={ d } pathLength={ 1 } key={ `p${ i }` } />
					) ) }
					<path className="HowItWorks-Link-Pulse HowItWorks-Link-Pulse-Id" d={ layout.idLink } pathLength={ 1 } />

					{ /* The identifier going in, over the picture's link. */ }
					<g style={ place( layout.idIn ) } >
						<g className="HowItWorks-Id HowItWorks-IdIn" >
							<IdTag />
						</g>
					</g>

					<g style={ place( layout.source ) } >
						<Card bMarked={ false } />
						<text className="HowItWorks-Label" y={ CARD_H / 2 + 20 } >your image</text>
					</g>

					<g className="HowItWorks-Net HowItWorks-Encoder" style={ place( layout.encoder ) } >
						<Network layers={ ENCODER_LAYERS } gradientId="hiw-encoder" />
						<text className="HowItWorks-Label" y={ networkLabelY( ENCODER_LAYERS ) } >encoder</text>
					</g>

					<g style={ place( layout.marked ) } >
						<g className="HowItWorks-Marked" >
							<Card bMarked={ true } />
						</g>
						<text className="HowItWorks-Label" y={ CARD_H / 2 + 20 } >watermarked</text>
					</g>

					<g style={ place( layout.phone ) } >
						<Phone />
						<text className="HowItWorks-Label" y={ PHONE_H / 2 + 20 } >screenshot, crop</text>
					</g>

					<g className="HowItWorks-Net HowItWorks-Decoder" style={ place( layout.decoder ) } >
						<Network layers={ DECODER_LAYERS } gradientId="hiw-decoder" />
						<text className="HowItWorks-Label" y={ networkLabelY( DECODER_LAYERS ) } >decoder</text>
					</g>

					{ /* The identifier read, out of the decoder. */ }
					<g style={ place( layout.idOut ) } >
						<g className="HowItWorks-Id HowItWorks-IdOut" >
							<IdTag />
						</g>
					</g>

					<g className="HowItWorks-User" style={ place( layout.user ) } >
						<circle className="HowItWorks-User-Ring" r={ USER_R + 7 } />
						<circle className="HowItWorks-User-Face" r={ USER_R } />
						<circle className="HowItWorks-User-Stroke" cy="-6" r="6.5" />
						<path className="HowItWorks-User-Stroke" d="M-13 15 a13 13 0 0 1 26 0" />
						<text className="HowItWorks-Label HowItWorks-User-Label" y={ USER_R + 22 } >Fan B</text>
					</g>

					<g style={ place( layout.answer ) } >
						<g className="HowItWorks-Answer" >
							<rect x="-66" y="-15" width="132" height="30" rx="15" />
							<text y="4.5" >Given to Fan B</text>
						</g>
					</g>
				</svg>
			</div>

			<Steps titles={ STEPS } durationsMs={ STAGE_DURATIONS_MS } player={ player } fadeDelay="0.1" />

		</div>
	)
}
